import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Phone, MessageCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, API, PROJECTS } from "@/data/site";
import { EASE } from "./motion";
import { useLead } from "@/App";

export function LeadForm({ source, compact = false, onSuccess }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source }),
      });
      if (!res.ok) throw new Error();
      toast.success("Thank you! We'll call you within 2 hours.");
      setForm({ name: "", phone: "", email: "" });
      onSuccess?.();
    } catch {
      toast.error("Something went wrong — please WhatsApp us instead");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "h-12 w-full rounded-lg border border-[#1B1B1B]/15 bg-[#F7F5F2] px-4 text-sm text-[#1B1B1B] placeholder:text-[#2C2C2C]/40 outline-none transition-colors duration-300 focus:border-[#B68D40]";

  return (
    <form onSubmit={submit} data-testid="lead-form" className="space-y-4">
      <input data-testid="lead-name-input" className={inputCls} placeholder="Your name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      <input data-testid="lead-phone-input" className={inputCls} placeholder="Phone / WhatsApp *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
      {!compact && (
        <input data-testid="lead-email-input" className={inputCls} placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      )}
      <button
        type="submit"
        disabled={sending}
        data-testid="lead-submit-btn"
        className="group flex w-full items-center justify-center gap-3 rounded-full bg-[#1B1B1B] py-4 text-sm font-semibold uppercase tracking-widest text-[#F7F5F2] transition-colors duration-300 hover:bg-[#B68D40] hover:text-[#1B1B1B] disabled:opacity-60"
      >
        {sending ? "Reserving…" : "Reserve My Free Session"}
        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>
      <p className="text-center text-[11px] text-[#2C2C2C]/45">No spam. One call, one designer, zero pressure.</p>
    </form>
  );
}

export default function LeadPopup({ open, source, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="consultation-modal"
          className="fixed inset-0 z-[90] flex items-center justify-center bg-[#1B1B1B]/70 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="grid w-full max-w-3xl grid-cols-1 overflow-hidden rounded-2xl bg-[#F7F5F2] md:grid-cols-2"
            initial={{ scale: 0.92, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 20, opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative hidden md:block">
              <img src={PROJECTS[4].img} alt="Diamond-cove master bedroom by Bengaluru Interiors" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-[#1B1B1B]/35" />
              <p className="absolute bottom-8 left-8 right-8 font-serif2 text-2xl italic leading-snug text-[#F7F5F2]">
                "Your home should tell the story of who you are."
              </p>
            </div>
            <div className="relative p-8 md:p-10">
              <button onClick={onClose} data-testid="consultation-close-btn" className="absolute right-5 top-5 flex h-9 w-9 items-center justify-center rounded-full border hairline text-[#2C2C2C]/60 transition-colors hover:border-[#B68D40] hover:text-[#B68D40]" aria-label="Close">
                <X size={16} />
              </button>
              <p className="font-num text-[10px] uppercase tracking-[0.3em] text-[#B68D40]">Free · 45 minutes</p>
              <h3 className="mt-3 font-display text-3xl tracking-tight text-[#1B1B1B]">
                Design consultation, <span className="italic text-[#B68D40]">on us.</span>
              </h3>
              <p className="mt-2 text-xs leading-relaxed text-[#2C2C2C]/55">
                Site visit, design direction and a transparent quote — free, no obligation.
              </p>
              <div className="mt-6">
                <LeadForm source={source} compact onSuccess={onSuccessClose(onClose)} />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const onSuccessClose = (onClose) => () => setTimeout(onClose, 600);

export function LeadTriggers() {
  const { openConsult } = useLead();

  useEffect(() => {
    const shownKey = "bi_popup_shown";
    if (sessionStorage.getItem(shownKey)) return;

    const timer = setTimeout(() => {
      if (!sessionStorage.getItem(shownKey)) {
        sessionStorage.setItem(shownKey, "1");
        openConsult("timed_30s");
      }
    }, 30000);

    const onExit = (e) => {
      if (e.clientY <= 0 && !sessionStorage.getItem(shownKey)) {
        sessionStorage.setItem(shownKey, "1");
        openConsult("exit_intent");
      }
    };
    document.addEventListener("mouseleave", onExit);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mouseleave", onExit);
    };
  }, [openConsult]);

  return null;
}

export function FloatingUI() {
  const { openConsult } = useLead();
  const [pastHero, setPastHero] = useState(false);

  useEffect(() => {
    const onScroll = () => setPastHero(window.scrollY > window.innerHeight * 0.7);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.a
        href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent("Hi Sohan! I'd like to discuss my home interiors.")}`}
        target="_blank"
        rel="noreferrer"
        data-testid="whatsapp-float-btn"
        className="fixed bottom-24 right-5 z-[55] flex h-14 w-14 items-center justify-center rounded-full bg-[#B68D40] text-[#1B1B1B] shadow-[0_12px_32px_rgba(182,141,64,0.4)] md:bottom-8 md:right-8"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 3.8, type: "spring", stiffness: 200, damping: 14 }}
        aria-label="Chat on WhatsApp"
      >
        <MessageCircle size={24} />
        <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#B68D40]/40" />
      </motion.a>

      <AnimatePresence>
        {pastHero && (
          <motion.div
            data-testid="sticky-bottom-cta"
            className="glass fixed inset-x-0 bottom-0 z-50 border-t hairline md:hidden"
            initial={{ y: 80 }}
            animate={{ y: 0 }}
            exit={{ y: 80 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <div className="flex gap-3 px-5 py-3">
              <a
                href={`tel:${CONTACT.phoneRaw}`}
                data-testid="sticky-call-btn"
                className="flex flex-1 items-center justify-center gap-2 rounded-full border hairline py-3 text-xs font-semibold uppercase tracking-widest text-[#1B1B1B]"
              >
                <Phone size={14} /> Call
              </a>
              <button
                onClick={() => openConsult("sticky_cta")}
                data-testid="sticky-consult-btn"
                className="flex-[2] rounded-full bg-[#1B1B1B] py-3 text-xs font-semibold uppercase tracking-widest text-[#F7F5F2]"
              >
                Free Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
